import React from 'react';
import { useProductsViewModel } from '../viewmodels/useProductsViewModel';
import { useCategoriesViewModel } from '../viewmodels/useCategoriesViewModel';
import { useTheme } from '../themes/ThemeProvider';
import ProductGrid from '../components/product/ProductGrid';

const ProductsPage: React.FC = () => {
  const {
    products,
    isLoading,
    error,
    loadMore,
    hasNextPage,
    currentPage,
    totalPages,
    filters,
    updateFilters,
    updateSearch,
    clearFilters,
  } = useProductsViewModel();
  const { topLevelCategories } = useCategoriesViewModel();
  const { branding } = useTheme();

  const listingConfig = branding?.productListing;
  const itemNoun = listingConfig?.itemNoun || 'product';
  const searchPlaceholder = listingConfig?.searchPlaceholder || `Search ${itemNoun}s...`;
  const showSearch = listingConfig?.filters?.showSearch ?? true;
  const showSort = listingConfig?.filters?.showSort ?? true;
  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  const pluralTitle = `All ${capitalize(itemNoun)}s`;

  // Grid column count at each breakpoint — defaults match existing layout
  const cols = {
    mobile: listingConfig?.columns?.mobile ?? 1,
    tablet: listingConfig?.columns?.tablet ?? 2,
    desktop: listingConfig?.columns?.desktop ?? 3,
    wide: listingConfig?.columns?.wide ?? 4,
  };
  // Tailwind needs these classes expanded so JIT can find them.
  // Stable mapping from numeric cols to explicit class names.
  const colsClassFor = (bp: 'mobile' | 'tablet' | 'desktop' | 'wide'): string => {
    const n = cols[bp];
    const map: Record<number, Record<string, string>> = {
      1: { mobile: 'grid-cols-1', tablet: 'md:grid-cols-1', desktop: 'lg:grid-cols-1', wide: 'xl:grid-cols-1' },
      2: { mobile: 'grid-cols-2', tablet: 'md:grid-cols-2', desktop: 'lg:grid-cols-2', wide: 'xl:grid-cols-2' },
      3: { mobile: 'grid-cols-3', tablet: 'md:grid-cols-3', desktop: 'lg:grid-cols-3', wide: 'xl:grid-cols-3' },
      4: { mobile: 'grid-cols-4', tablet: 'md:grid-cols-4', desktop: 'lg:grid-cols-4', wide: 'xl:grid-cols-4' },
    };
    return map[n]?.[bp] ?? '';
  };
  const gridClassName = [
    colsClassFor('mobile'),
    colsClassFor('tablet'),
    colsClassFor('desktop'),
    colsClassFor('wide'),
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className="container mx-auto px-lg py-3xl"
      style={{ maxWidth: 'var(--container-max-width)' }}
      data-testid="products-page"
    >
      {/* Page Header */}
      <div className="mb-3xl text-center">
        <h1 className="font-serif text-4xl md:text-5xl font-semibold text-primary mb-md">
          {pluralTitle}
        </h1>
        <p className="text-text-secondary text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
          {branding?.store?.description || `Explore our complete selection of ${itemNoun}s.`}
        </p>
      </div>

      {/* Filters and Search */}
      <div className="bg-[var(--color-bg)] border border-border rounded-sm p-lg mb-2xl">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-lg items-end">
          {showSearch && (
            <div>
              <label htmlFor="product-search" className="block text-text-secondary font-medium mb-sm text-sm">
                Search
              </label>
              <input
                id="product-search"
                type="text"
                placeholder={searchPlaceholder}
                value={filters.search || ''}
                onChange={(e) => updateSearch(e.target.value)}
                className="w-full px-md py-sm border border-border rounded-sm text-sm focus:outline-none focus:border-primary transition-colors"
                data-testid="product-search-input"
              />
            </div>
          )}

          {/* Category Filter */}
          <div>
            <label htmlFor="product-category" className="block text-text-secondary font-medium mb-sm text-sm">
              Category
            </label>
            <select
              id="product-category"
              value={filters.category || ''}
              onChange={(e) => updateFilters({ category: e.target.value || undefined })}
              className="w-full px-md py-sm border border-border rounded-sm text-sm bg-[var(--color-bg)] focus:outline-none focus:border-primary transition-colors"
              data-testid="product-category-select"
            >
              <option value="">All Categories</option>
              {topLevelCategories.map((cat) => {
                const children = (cat.category_children || [])
                  .filter((c) => c.is_active && !c.is_internal)
                  .sort((a, b) => a.rank - b.rank);
                return (
                  <React.Fragment key={cat.id}>
                    <option value={cat.id}>{cat.name}</option>
                    {children.map((child) => (
                      <option key={child.id} value={child.id}>
                        &nbsp;&nbsp;{child.name}
                      </option>
                    ))}
                  </React.Fragment>
                );
              })}
            </select>
          </div>

          {showSort && (
            <div>
              <label htmlFor="product-sort" className="block text-text-secondary font-medium mb-sm text-sm">
                Sort By
              </label>
              <select
                id="product-sort"
                value={filters.sortBy || 'created_at'}
                onChange={(e) => updateFilters({ sortBy: e.target.value as any })}
                className="w-full px-md py-sm border border-border rounded-sm text-sm bg-[var(--color-bg)] focus:outline-none focus:border-primary transition-colors"
                data-testid="product-sort-select"
              >
                <option value="created_at">Newest First</option>
                <option value="title">Name A-Z</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
              </select>
            </div>
          )}

          {/* Clear Filters */}
          <div>
            <button
              onClick={clearFilters}
              className="w-full px-md py-sm bg-transparent text-text-secondary border border-border rounded-sm text-sm font-medium transition-all hover:border-primary hover:text-primary"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Active Filters Display */}
        {(filters.search || filters.category) && (
          <div className="mt-lg pt-lg border-t border-border">
            <div className="text-sm text-text-secondary mb-sm">
              Active filters:
            </div>
            <div className="flex gap-sm flex-wrap">
              {filters.search && (
                <span className="px-3 py-1 bg-[rgba(var(--color-primary-rgb),0.08)] text-primary border border-[rgba(var(--color-primary-rgb),0.2)] rounded-full text-xs flex items-center gap-2">
                  Search: "{filters.search}"
                  <button
                    onClick={() => updateSearch('')}
                    className="bg-transparent border-none text-inherit cursor-pointer text-sm"
                    aria-label="Clear search filter"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.category && (
                <span className="px-3 py-1 bg-[rgba(var(--color-primary-rgb),0.08)] text-primary border border-[rgba(var(--color-primary-rgb),0.2)] rounded-full text-xs flex items-center gap-2">
                  Category: {filters.category}
                  <button
                    onClick={() => updateFilters({ category: undefined })}
                    className="bg-transparent border-none text-inherit cursor-pointer text-sm"
                    aria-label="Clear category filter"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Results Count */}
      {!isLoading && (
        <div className="mb-lg text-text-secondary text-sm">
          {products.length === 0
            ? `No ${itemNoun}s found`
            : `${products.length} ${itemNoun}${products.length !== 1 ? 's' : ''} found`}
        </div>
      )}

      {/* Products Grid */}
      <ProductGrid
        products={products}
        isLoading={isLoading}
        error={error}
        gridClassName={gridClassName || undefined}
      />
      {products.length > 0 && (
        <div className="mt-2xl flex flex-col items-center gap-sm">
          <div className="text-sm text-text-secondary">
            Page {currentPage} of {Math.max(totalPages, 1)}
          </div>
          {hasNextPage ? (
            <button
              onClick={loadMore}
              disabled={isLoading}
              className="px-xl py-md bg-primary text-[var(--color-text-on-primary)] rounded-sm text-sm font-medium transition-colors hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Loading…' : `Load More ${capitalize(itemNoun)}s`}
            </button>
          ) : (
            <div className="text-sm text-text-secondary text-center">
              You’ve reached the end of the results.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductsPage;
